import { useEffect, useLayoutEffect, useRef } from 'react';
import { normalizeJotformHtml } from '../utils/jotformEmbed';
import { mountJotformEmbeds } from '../utils/mountJotformEmbeds';

/**
 * Renders post HTML and mounts Jotform iframes after the DOM is updated.
 * Uses manual innerHTML so React re-renders do not wipe injected embeds.
 */
export function useJotformEmbeds(containerRef, html) {
  const mountedHtmlRef = useRef('');

  useLayoutEffect(() => {
    const container = containerRef?.current;
    if (!container || !html) return;

    if (mountedHtmlRef.current !== html) {
      container.innerHTML = normalizeJotformHtml(html);
      mountedHtmlRef.current = html;
      container.querySelectorAll('.jotform-embed[data-loaded="true"]').forEach((el) => {
        el.removeAttribute('data-loaded');
      });
    }
  }, [containerRef, html]);

  useEffect(() => {
    const container = containerRef?.current;
    if (!container || !html) return;

    let cancelled = false;

    const mount = () => {
      if (!cancelled) {
        mountJotformEmbeds(container);
      }
    };

    mount();

    // Re-mount after enter animations / mobile layout so iframe gets correct dimensions.
    const retryTimer = window.setTimeout(mount, 800);
    const resizeHandler = () => mount();
    window.addEventListener('orientationchange', resizeHandler);

    return () => {
      cancelled = true;
      window.clearTimeout(retryTimer);
      window.removeEventListener('orientationchange', resizeHandler);
    };
  }, [containerRef, html]);
}

export default useJotformEmbeds;
